/* eslint-disable camelcase */
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FormikProps } from 'formik';
import {
  withInject,
  Action,
} from '@piwikpro/platform';
import {
  Text,
  Stack,
  Card,
  Spinner,
  withTranslation,
  Information,
  Link,
  Trans,
  Layout,
  TFunction,
} from '@piwikpro/ui-components';
import { ContainerSettings } from '../../ContainerSettings';
import FailMessage from '../FailMessage';
import { ContainerSettingsState } from '../../redux';
import { DomainSetting } from '../../redux/appDomainSettings';

const mapStateToProps = (state: ContainerSettingsState) => ({
  isLoading: state.containerSettings.app.spaTracking.isLoading,
  failed: state.containerSettings.app.spaTracking.failed,
  value: state.containerSettings.app.spaTracking.value,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  containerSettings: ContainerSettings
}

export interface Props extends ReduxProps, InjectedProps {
  appId: string
  controlWrapper?: typeof Action
  t: TFunction
}

interface FormValues {
  spaEnabled: boolean
}

export class SpaTracking extends React.Component<Props> {
  private settingName: DomainSetting = 'spa_tracking';

  constructor(props: Props) {
    super(props);

    this.handleSwitcherSubmit = this.handleSwitcherSubmit.bind(this);
  }

  async componentDidMount() {
    const { containerSettings, appId } = this.props;

    try {
      await containerSettings.getAppSetting(appId, this.settingName);
      // eslint-disable-next-line no-empty
    } catch {}
  }

  async handleSwitcherSubmit(
    values: FormValues,
    { setFieldValue, setSubmitting }: FormikProps<FormValues>,
  ) {
    const {
      containerSettings,
      appId,
    } = this.props;

    try {
      await containerSettings.updateAppSetting(appId, this.settingName, {
        value: {
          is_enabled: values.spaEnabled,
        },
      });
    } catch {
      setFieldValue('spaEnabled', !values.spaEnabled, false);
    }

    setSubmitting(false);
  }

  render() {
    const {
      value,
      isLoading,
      failed,
      controlWrapper,
      t,
    } = this.props;
    const ActionWrapper = controlWrapper || Action;

    return (
      <Layout>
        <Layout.AnnotatedSection title={t('spaTracking.section.header')} />
        <Layout.Column dominant>
          {isLoading && (
            <Card>
              <Card.Section>
                <Spinner />
              </Card.Section>
            </Card>
          )}
          {failed && <FailMessage />}
          {value && (
            <Card>
              <Card.Section>
                <Stack vertical spacing="wide">
                  <Stack.Item fill>
                    <Stack wrap verticalAlignment="center">
                      <Stack.Item fill>
                        <Stack vertical spacing="narrow">
                          <Stack.Item>
                            <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('spaTracking.section.title')}</Text>
                          </Stack.Item>
                          <Stack.Item>
                            <Text as="p">{t('spaTracking.section.description')}</Text>
                          </Stack.Item>
                        </Stack>
                      </Stack.Item>
                      <Stack.Item>
                        <ActionWrapper.AutoSavedSwitcherControl
                          id="container-settings-spa-tracking-switcher"
                          initialValue={value.is_enabled}
                          name="spaEnabled"
                          onSubmit={this.handleSwitcherSubmit}
                          disabledActionTooltipText={t('app.details.settings.general.only-for-users-with-analytics-module.block-reason')}
                        />
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  <Stack.Item fill>
                    <Information type="info">
                      <Trans
                        i18nKey="container-settings:spa-tracking-switcher.help-2"
                        components={[
                          <Link
                            size="small"
                            href="https://developers.piwik.pro/en/latest/data_collection/web/frameworks/index.html"
                          >
                            help center article
                          </Link>,
                        ]}
                      />
                    </Information>
                  </Stack.Item>
                </Stack>
              </Card.Section>
            </Card>
          )}
        </Layout.Column>
      </Layout>
    );
  }
}

export default connector(withInject<InjectedProps>({
  containerSettings: 'ContainerSettingsCrate.ContainerSettings',
})(withTranslation(['container-settings', 'administration'])(SpaTracking)));
